import React, { useEffect, useState, useRef } from 'react';
import {
  Box, Typography, Paper, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl
} from '@mui/material';
import PaginacionControl from '../components/PaginacionControl';
import Autocomplete from '@mui/material/Autocomplete';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/GetApp';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { GridOn } from '@mui/icons-material';
import axios from 'axios';


// Los tipos de documento ahora se obtienen dinámicamente


export default function SeriesSucursal() {
  const [sucursales, setSucursales] = useState([]);
  const [series, setSeries] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const [sucursalId, setSucursalId] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ tipo_documento: '', serie: '', correlativo_actual: 1 });
  const [editId, setEditId] = useState(null);
  const [tiposDocumento, setTiposDocumento] = useState([]);
  const tableRef = useRef();
  // Obtener tipos de documento dinámicamente
  useEffect(() => {
    axios.get('/api/tipo-documento').then(res => {
      // Mapear para el Autocomplete: value = codigo_sunat, label = nombre
      setTiposDocumento(res.data.map(td => ({
        value: td.sunat || td.codigo_sunat || td.id,
        label: td.nombre || td.descripcion || td.abreviatura || td.sunat
      })));
    });
  }, []);

  useEffect(() => {
    axios.get('/api/sucursales').then(res => setSucursales(res.data));
  }, []);

  useEffect(() => {
    if (sucursalId) {
      axios.get(`/api/series/sucursal/${sucursalId}`).then(res => {
        setSeries(res.data);
        setFiltered(res.data);
      });
    } else {
      setSeries([]);
      setFiltered([]);
    }
  }, [sucursalId]);

  useEffect(() => {
    if (!search) {
      setFiltered(series);
    } else {
      setFiltered(
        series.filter(se =>
          (se.tipo_documento || '').toLowerCase().includes(search.toLowerCase()) ||
          (se.serie || '').toLowerCase().includes(search.toLowerCase())
        )
      );
    }
    setPage(0);
  }, [search, series]);

  const handleOpen = (serie = null) => {
    if (serie) {
      setForm({ tipo_documento: serie.tipo_documento, serie: serie.serie, correlativo_actual: serie.correlativo_actual });
      setEditId(serie.id);
    } else {
      setForm({ tipo_documento: '', serie: '', correlativo_actual: 1 });
      setEditId(null);
    }
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!sucursalId) return;
    try {
      if (editId) {
        await axios.put(`/api/series/${editId}`, form);
      } else {
        await axios.post('/api/series', { ...form, id_sucursal: sucursalId });
      }
      setOpen(false);
      axios.get(`/api/series/sucursal/${sucursalId}`).then(res => {
        setSeries(res.data);
        setFiltered(res.data);
      });
    } catch (err) { alert(err.response?.data?.error || 'Error al guardar'); }
  };

  const handleDelete = async id => {
    if (window.confirm('¿Eliminar serie?')) {
      try {
        await axios.delete(`/api/series/${id}`);
        axios.get(`/api/series/sucursal/${sucursalId}`).then(res => setSeries(res.data));
      } catch { alert('Error al eliminar'); }
    }
  };

  // Exportar a Excel
  const exportExcel = () => {
    if (!filtered.length) return;
    (async () => {
      const { exportGenericWorkbook } = await import('../utils/excel/genericWorkbook');
      const rows = filtered.map(se => ({
        id: se.id,
        tipo_documento: se.tipo_documento,
        serie: se.serie,
        correlativo_actual: se.correlativo_actual
      }));
      await exportGenericWorkbook({
        titulo: 'REPORTE - SERIES POR SUCURSAL',
        sheetName: 'SeriesSucursal',
        tableName: 'tblSeriesSucursal',
        fileName: 'SeriesSucursal',
        columns: [
          { key: 'id', label: 'ID', width: 8 },
          { key: 'tipo_documento', label: 'Tipo Documento', width: 22 },
          { key: 'serie', label: 'Serie', width: 14 },
          { key: 'correlativo_actual', label: 'Correlativo Actual', width: 20 }
        ],
        rows,
        searchText: search,
        usuario: ''
      });
    })();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1, mb: 4, width: '100%' }}>
      <Box sx={{ width: { xs: '100%', sm: '95%', md: '90%' }, maxWidth: 1800 }}>
        <Typography variant="h4" gutterBottom sx={{ textAlign: { xs: 'center', md: 'left' }, mb: 3, fontWeight: 'bold', color: '#222' }}>
          Series por Sucursal
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2, alignItems: 'center' }}>
          <Autocomplete
            options={sucursales}
            getOptionLabel={option => option?.nombre || ''}
            value={sucursales.find(s => s.id === sucursalId) || null}
            onChange={(_e, value) => setSucursalId(value ? value.id : '')}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={params => (
              <TextField {...params} label="Sucursal" margin="dense" sx={{ minWidth: 250, mt: 0, mb: 0 }} />
            )}
            sx={{ minWidth: 250, mt: 0, mb: 0, alignSelf: 'flex-start' }}
          />
          <TextField
            size="small"
            placeholder="Buscar..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            InputProps={{
              endAdornment: (
                <SearchIcon color="action" />
              )
            }}
            sx={{ flex: 1 }}
            disabled={!sucursalId}
          />
          <Button variant="contained" color="success" startIcon={<GridOn />} sx={{ minWidth: 120 }} onClick={exportExcel} disabled={!filtered.length}>
            Exportar Excel
          </Button>
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => handleOpen()} sx={{ minWidth: 140 }} disabled={!sucursalId}>
            Nueva Serie
          </Button>
          <TextField
            size="small"
            label="Mostrar"
            type="number"
            value={rowsPerPage}
            onChange={e => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
            sx={{ width: 100 }}
            disabled={!sucursalId}
          />
        </Box>
        {sucursalId && (
          <TableContainer component={Paper} sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', mt: 2, overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="center">Tipo Documento</TableCell>
                  <TableCell align="center">Serie</TableCell>
                  <TableCell align="center">Correlativo Actual</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(rowsPerPage === -1 ? filtered : filtered.slice(page * rowsPerPage, (page + 1) * rowsPerPage)).map(se => (
                  <TableRow key={se.id}>
                    <TableCell align="center">{se.tipo_documento}</TableCell>
                    <TableCell align="center">{se.serie}</TableCell>
                    <TableCell align="center">{se.correlativo_actual}</TableCell>
                    <TableCell align="center">
                      <IconButton color="primary" onClick={() => handleOpen(se)}><EditIcon /></IconButton>
                      <IconButton color="error" onClick={() => handleDelete(se.id)}><DeleteIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {sucursalId && (
          <PaginacionControl page={page} rowsPerPage={rowsPerPage} count={filtered.length} onPageChange={setPage} />
        )}
      </Box>
      <Dialog open={open} onClose={(_, r) => r !== 'backdropClick' && handleClose()} disableEscapeKeyDown={false} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0, mb: 0 }}>
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 0 }}>
            {editId ? 'Editar Serie' : 'Nueva Serie'}
          </Typography>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Autocomplete
              options={tiposDocumento}
              getOptionLabel={option => option.label}
              value={tiposDocumento.find(td => td.value === form.tipo_documento) || null}
              onChange={(_e, value) => setForm(f => ({ ...f, tipo_documento: value ? value.value : '' }))}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={params => (
                <TextField {...params} label="Tipo Documento" margin="normal" required />
              )}
              sx={{ mt: 0 }}
              disabled={tiposDocumento.length === 0}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Serie"
                name="serie"
                value={form.serie}
                onChange={handleChange}
                margin="normal"
                required
                sx={{ flex: 3 }}
              />
              <TextField
                label="Correlativo Actual"
                name="correlativo_actual"
                value={form.correlativo_actual}
                onChange={handleChange}
                margin="normal"
                type="number"
                required
                sx={{ flex: 7 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="error" variant="contained" startIcon={<CloseIcon />}>Cancelar</Button>
            <Button type="submit" variant="contained" color="primary" startIcon={<SaveIcon />}>Guardar</Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}
